import { ParsedJWTToken } from '../types/auth';
import { getIdMeBaseApiUrl } from './UrlConstants';
import { fetchUserGroups } from './groupUtils';
import { SessionStorage } from '@azure/msal-browser';
import { useId } from 'react';
import { useIdentityAppService } from '../services/IdentityServices';

// Token Management
export const parseJWTToken = (token: string): ParsedJWTToken | null => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch (error) {
        console.error("Error decoding JWT token", error);
        return null;
    }
};

export const getStoredToken = (): ParsedJWTToken | null => {
    const externalToken = sessionStorage.getItem("external_token");
    if (externalToken) {
        try {
            return JSON.parse(externalToken);
        } catch {
            return null;
        }
    }
    return null;
};

export const storeToken = (token: ParsedJWTToken): void => {
    sessionStorage.setItem("external_token", JSON.stringify(token));
    // Trigger custom event to notify components about token storage
    window.dispatchEvent(new Event('sessionStorageUpdate'));
};

export const clearTokens = (): void => {
    sessionStorage.removeItem("external_token");
    sessionStorage.removeItem("internal_token");
    sessionStorage.removeItem("external_groups");
    sessionStorage.removeItem("currentUserData");
    // Trigger custom event to notify components about token removal
    window.dispatchEvent(new Event('sessionStorageUpdate'));
};

// Authentication State
export const checkUserAuthentication = (): boolean => {
    const externalToken = sessionStorage.getItem("external_token");
    const internalToken = sessionStorage.getItem("internal_token");
    return Boolean(externalToken || internalToken);
};

export const isExternalUser = (): boolean => {
    return Boolean(sessionStorage.getItem("external_token"));
};

export const isInternalUser = (): boolean => {
    return Boolean(sessionStorage.getItem("internal_token"));
};

// Process B2C Authorization Code
export const processB2CAuthCode = async (authCode: string): Promise<ParsedJWTToken | null> => {
    try {
        const tokenResp = await useIdentityAppService().getB2cAuthCode(authCode);

        const idToken = tokenResp.data?.id_token as string | undefined;
        if (!idToken) {
            console.error('No id_token found in token response');
            return null;
        }

        const parsed = parseJWTToken(idToken);
        if (!parsed) return null;

        // Store token
        sessionStorage.setItem("msalToken", idToken);
        storeToken(parsed);

        // Fetch and store user groups
        if (parsed.sub) {
            try {
                const apiGroups = await fetchUserGroups(parsed.sub);
                sessionStorage.setItem("external_groups", apiGroups.join(',') || '');
                console.log("Set external_groups in sessionStorage with API data:", apiGroups);
                // Trigger update for components that need group info
                window.dispatchEvent(new Event('sessionStorageUpdate'));
            } catch (error) {
                console.error("Failed to fetch groups from API, falling back to token groups:", error);
                sessionStorage.setItem("external_groups", parsed.groupNames?.join(',') || '');
                window.dispatchEvent(new Event('sessionStorageUpdate'));
            }
        } else {
            console.warn("No 'sub' field found in token, falling back to token groups");
            sessionStorage.setItem("external_groups", parsed.groupNames?.join(',') || '');
            window.dispatchEvent(new Event('sessionStorageUpdate'));
        }

        // Call post sign-in API asynchronously (don't block user experience)
        useIdentityAppService().postSessionInfo().then(response => {
            console.log("Post B2C sign-in API response:", response.data);
            // Set a flag indicating the API call completed
            sessionStorage.setItem('post_signin_completed', 'true');
        }).catch(apiError => {
            console.error("Post B2C sign-in API call failed:", apiError);
            // Even if it fails, don't block the user
            sessionStorage.setItem('post_signin_completed', 'true');
        });

        console.log("Post sign-in API initiated in background - user can continue");

        return parsed;
    } catch (error) {
        console.error('Authorization code exchange failed', error);
        return null;
    }
};

// User Data Management
interface UserSearchResponse {
    success: boolean;
    message: string;
    users: any[];
    count: number;
    timestamp: string;
}

// Lock to prevent multiple simultaneous API calls
let searchAndUpdateInProgress = false;
let searchAndUpdatePromise: Promise<any | null> | null = null;

export const searchAndStoreCurrentUser = async (): Promise<any | null> => {
    // If a request is already in progress, return the same promise to avoid duplicate calls
    if (searchAndUpdateInProgress && searchAndUpdatePromise) {
        console.log("Search-and-update already in progress, reusing existing request");
        return searchAndUpdatePromise;
    }

    // Mark as in progress and create new promise
    searchAndUpdateInProgress = true;
    searchAndUpdatePromise = performSearchAndUpdate();

    try {
        const result = await searchAndUpdatePromise;
        return result;
    } finally {
        // Release lock after completion
        searchAndUpdateInProgress = false;
        searchAndUpdatePromise = null;
    }
};

const performSearchAndUpdate = async (): Promise<any | null> => {
    try {
        const externalToken = getStoredToken();
        if (!externalToken) {
            console.log("No external token found, skipping user search and update");
            return null;
        }

        // Validate required fields for search-and-update endpoint
        if (!externalToken.username || !externalToken.sub) {
            console.error("Missing required fields: username or sub (userIdpGuid) in token");
            return null;
        }

        // Build request body from token data
        const requestBody: any = {
            // Required search parameters (immutable keys)
            userLogin: externalToken.username.trim(),
            userIdpGuid: externalToken.sub.trim(),

            // Updatable fields from token
            firstName: externalToken.given_name?.trim() || null,
            lastName: externalToken.family_name?.trim() || null,
            userEmail: externalToken.email?.trim() ? btoa(externalToken.email.trim()) : null, // base64 encode
            userPhone: externalToken.mobile?.trim() || null,
            addressLine1: externalToken.streetAddress?.trim() || null,
            addressLine2: externalToken.extension_addressLine2?.trim() || null,
            state: externalToken.state?.trim() || null,
            zip: externalToken.extension_zipCode?.trim() || null,
            country: externalToken.country?.trim() || null,

            // Audit field
            modifiedBy: externalToken.username?.trim() || 'SYSTEM'
        };

        // Remove null/undefined values to avoid sending unnecessary data
        Object.keys(requestBody).forEach(key => {
            if (requestBody[key] === null || requestBody[key] === undefined) {
                delete requestBody[key];
            }
        });

        const url = getIdMeBaseApiUrl() + '/user/search-and-update';

        console.log("Searching and updating user with URL:", url);
        console.log("Request body:", { ...requestBody, userEmail: requestBody.userEmail ? '[BASE64_ENCODED]' : null });

        const response = await useIdentityAppService().updateUserSearch(JSON.stringify(requestBody));

        if (response.ok) {
            const data: UserSearchResponse = await response.json();
            console.log("User search-and-update response:", data);

            if (data.success && data.users && data.users.length > 0) {
                const userData = data.users[0];
                sessionStorage.setItem('currentUserData', JSON.stringify(userData));
                console.log("User data stored in session:", userData);
                console.log("Update message:", data.message);

                // Trigger update for components that need user data
                window.dispatchEvent(new Event('sessionStorageUpdate'));
                return userData;
            } else {
                console.log("No user found or created in search-and-update results");
                return null;
            }
        } else {
            const errorData = await response.json().catch(() => ({}));
            console.error("Failed to search and update user:", errorData);
            return null;
        }
    } catch (error) {
        console.error("Error in search-and-update user:", error);
        return null;
    }
};

export const getCurrentUserData = (): any | null => {
    const userData = sessionStorage.getItem('currentUserData');
    if (userData) {
        try {
            return JSON.parse(userData);
        } catch {
            return null;
        }
    }
    return null;
};

// Check if post-signin API has completed (for operations that may need it)
export const isPostSigninCompleted = (): boolean => {
    return sessionStorage.getItem('post_signin_completed') === 'true';
};

// Wait for post-signin API if needed (with timeout)
export const waitForPostSigninIfNeeded = async (timeoutMs: number = 5000): Promise<boolean> => {
    if (isPostSigninCompleted()) {
        return true;
    }

    return new Promise((resolve) => {
        const startTime = Date.now();
        const checkInterval = setInterval(() => {
            if (isPostSigninCompleted() || (Date.now() - startTime) > timeoutMs) {
                clearInterval(checkInterval);
                resolve(isPostSigninCompleted());
            }
        }, 100);
    });
};

// Navigation
export const redirectToLogin = (): void => {
    window.location.href = "/";
};

// Session Flags
export const isLogoutInProgress = (): boolean => {
    return sessionStorage.getItem("logout_in_progress") === "true";
};

export const setLogoutInProgress = (inProgress: boolean): void => {
    if (inProgress) {
        sessionStorage.setItem("logout_in_progress", "true");
    } else {
        sessionStorage.removeItem("logout_in_progress");
    }
};

export const isADLoginInProgress = (): boolean => {
    return sessionStorage.getItem("ad_login_in_progress") === "true";
};

export const setADLoginInProgress = (inProgress: boolean): void => {
    if (inProgress) {
        sessionStorage.setItem("ad_login_in_progress", "true");
    } else {
        sessionStorage.removeItem("ad_login_in_progress");
    }
};

export const isAuthInProgress = (): boolean => {
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace("#", ""));
    return urlParams.has("id_token") || hashParams.has("id_token") || urlParams.has("code") || hashParams.has("code");
};

export const handleUnauthenticatedUser = (): void => {
    redirectToLogin();
};